/**
 * ═══════════════════════════════════════════════════════════════
 * useAdminTheme — Centralized Design Tokens for Admin Panels
 * ═══════════════════════════════════════════════════════════════
 *
 * Single source of truth for the "Yin-Yang" admin aesthetic:
 *   → Sidebar: always dark navy (Command Center feel)
 *   → Content: always light (clean, breathable, Apple-inspired)
 *
 * Used by: AdminDashboard, GroupAdminDashboard, AdminHub
 * Philosophy: "Branding Mamalón" — Flat Depth, minimal shadows,
 *             content-first contrast, premium typography.
 */

/** Content area tokens — always light, clean, breathable */
export const adminTokens = {
    // ── Layout ──
    bg: 'bg-[#F7F8FA]',

    // ── Typography ──
    textPrimary: 'text-slate-900',
    textSecondary: 'text-slate-600',
    textMuted: 'text-slate-400',
    textAccent: 'text-indigo-600',

    // ── Cards (Flat Depth: minimal shadow, subtle border) ──
    cardBg: 'bg-white',
    cardBorder: 'border border-slate-200/70',
    cardShadow: 'shadow-[0_1px_3px_rgba(0,0,0,0.04)]',
    cardRadius: 'rounded-2xl',
    cardHover: 'hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:border-slate-300/80',
    cardTransition: 'transition-all duration-300 ease-out',

    // ── Premium Card (for KPIs / hero cards) ──
    premiumCard: 'bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(0,0,0,0.04)]',
    premiumCardHover: 'hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-[2px]',

    // ── Sidebar — always dark navy ──
    sidebarBg: 'bg-gradient-to-b from-[#0B1628] via-[#0F1D35] to-[#0B1628]',
    sidebarBorder: 'border-white/[0.06]',
    sidebarText: 'text-slate-400',
    sidebarTextHover: 'text-slate-200',
    sidebarActive: 'bg-white/[0.08] text-white border-l-[3px] border-indigo-400',
    sidebarHover: 'hover:bg-white/[0.04]',
    sidebarDivider: 'border-white/[0.06]',

    // ── Dividers & Borders ──
    divider: 'border-slate-200/60',
    borderTop: 'border-t border-slate-200/60',
    borderBottom: 'border-b border-slate-200/60',

    // ── Inputs ──
    inputBg: 'bg-slate-50',
    inputBorder: 'border-slate-200',
    inputFocus: 'focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/20',

    // ── Semantic Colors ──
    success: 'text-emerald-600',
    successBg: 'bg-emerald-50',
    successBorder: 'border-emerald-200',
    warning: 'text-amber-600',
    warningBg: 'bg-amber-50',
    warningBorder: 'border-amber-200',
    danger: 'text-red-600',
    dangerBg: 'bg-red-50',
    dangerBorder: 'border-red-200',
    info: 'text-blue-600',
    infoBg: 'bg-blue-50',
    infoBorder: 'border-blue-200',

    // ── Section Labels (uppercase micro-labels) ──
    sectionLabel: 'text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400',

    // ── Navbar ──
    navbarBg: 'bg-white/90 backdrop-blur-2xl border-b border-slate-200/60',
} as const;

/** Shorthand: compose a full card class string */
export const cardClass = `${adminTokens.cardBg} ${adminTokens.cardRadius} ${adminTokens.cardBorder} ${adminTokens.cardShadow}`;

/** Shorthand: compose a premium interactive card */
export const premiumCardClass = `${adminTokens.premiumCard} ${adminTokens.premiumCardHover} ${adminTokens.cardTransition}`;

/**
 * Legacy-compatible "t" alias — drop-in replacement for existing
 * useMemo token objects in AdminDashboard/GroupAdminDashboard/AdminHub.
 *
 * Maps old property names → new centralized tokens so migration
 * can happen file-by-file without breaking anything.
 */
export const t = {
    bg: adminTokens.bg,
    textMain: adminTokens.textSecondary,
    textWhite: adminTokens.textPrimary,
    textMuted: adminTokens.textMuted,
    cardBg: adminTokens.cardBg,
    cardBorder: adminTokens.cardBorder.replace('border ', ''),
    cardShadow: adminTokens.cardShadow,
    cardHoverBg: 'hover:bg-white',
    cardHoverBorder: 'hover:border-slate-300',
    navbarBg: adminTokens.navbarBg,
    sidebarBg: adminTokens.sidebarBg,
    sidebarBorder: adminTokens.sidebarBorder,
    sidebarText: adminTokens.sidebarText,
    sidebarTextHover: adminTokens.sidebarTextHover,
    sidebarActive: adminTokens.sidebarActive,
    sidebarHover: adminTokens.sidebarHover,
    sidebarDivider: adminTokens.sidebarDivider,
    inputBg: adminTokens.inputBg,
    divider: 'bg-slate-200/60',
    dividerBorder: 'divide-slate-200/60',
    borderBottom: adminTokens.borderBottom,
    borderTop: adminTokens.borderTop,
    tableHeader: 'bg-slate-50/80 border-slate-200/60',
    tableRowHover: 'hover:bg-slate-50/50',
    tableRowActive: 'bg-indigo-50/50',
} as const;

export type AdminThemeTokens = typeof t;
